import React, { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from '@lnr-v2/ui';
import { TagPill } from '../../atoms/TagPill';
import { useTags } from '../../../hooks/useTags';
import { prefetchTagStats } from '../../../lib/prefetchHelpers';
import type { Tag, TagCreateInput } from '../../../services/tagsService';
import { TagFormModal } from '../../organisms/TagFormModal';
import { toast } from '../../../utils/toast';
import styles from './TagSelector.module.css';

export interface TagSelectorProps {
  selectedTags: string[]; // Array of tag IDs
  onChange: (tagIds: string[]) => void;
  lang?: 'en' | 'fr';
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowCreate?: boolean; // Allow creating new tags inline
}

export const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onChange,
  lang = 'en',
  placeholder = 'Search and select tags...',
  disabled = false,
  className,
  allowCreate = true,
}) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { tags, loading, createTag } = useTags({
    search: searchQuery,
    active: true,
  });

  // Handle creating a new tag
  const handleCreateTag = async (data: TagCreateInput) => {
    try {
      await createTag(data);
      toast.success(`Tag "${data.nameEN}" created successfully`);
      setShowCreateModal(false);
      setSearchQuery('');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create tag';
      toast.error(errorMessage);
      throw error; // Re-throw to keep modal open
    }
  };

  // Prefetch tag stats when user hovers over the selector
  const handlePrefetchStats = () => {
    prefetchTagStats(queryClient);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get selected tag objects
  const selectedTagObjects = tags.filter((tag) =>
    selectedTags.includes(tag.id)
  );

  // Get available tags (not selected)
  const availableTags = tags.filter((tag) => !selectedTags.includes(tag.id));

  const handleToggleTag = (tagId: string) => {
    if (selectedTags.includes(tagId)) {
      onChange(selectedTags.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTags, tagId]);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    onChange(selectedTags.filter((id) => id !== tagId));
  };

  const getTagName = (tag: Tag) => {
    return lang === 'fr' ? tag.nameFR : tag.nameEN;
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${disabled ? styles.disabled : ''} ${className || ''}`}
      onMouseEnter={handlePrefetchStats}
    >
      {/* Selected tags */}
      <div
        className={styles.selectedTags}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {selectedTagObjects.length > 0 ? (
          selectedTagObjects.map((tag) => (
            <TagPill
              key={tag.id}
              label={getTagName(tag)}
              color={tag.color}
              icon={tag.icon}
              size="sm"
              removable
              onRemove={() => handleRemoveTag(tag.id)}
            />
          ))
        ) : (
          <span className={styles.placeholder}>{placeholder}</span>
        )}

        {/* Search input */}
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={selectedTagObjects.length > 0 ? '' : placeholder}
          disabled={disabled}
        />

        {/* Dropdown toggle icon */}
        <button
          type="button"
          className={styles.toggleButton}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size="sm" />
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className={styles.dropdown}>
          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner} />
              <span>Loading tags...</span>
            </div>
          ) : availableTags.length > 0 ? (
            <ul className={styles.list}>
              {availableTags.map((tag) => (
                <li
                  key={tag.id}
                  className={styles.listItem}
                  onClick={() => handleToggleTag(tag.id)}
                >
                  <div className={styles.tagInfo}>
                    {tag.icon && <Icon name={tag.icon} size="sm" />}
                    <span className={styles.tagName}>{getTagName(tag)}</span>
                    {tag.category && (
                      <span className={styles.category}>{tag.category}</span>
                    )}
                  </div>
                  {tag.color && (
                    <div
                      className={styles.colorDot}
                      style={{ backgroundColor: tag.color }}
                    />
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.empty}>
              <Icon name="tag" size="lg" />
              <p>No tags found</p>
              {searchQuery && (
                <button
                  type="button"
                  className={styles.clearButton}
                  onClick={() => setSearchQuery('')}
                >
                  Clear search
                </button>
              )}
              {allowCreate && (
                <button
                  type="button"
                  className={styles.createButton}
                  onClick={() => setShowCreateModal(true)}
                >
                  <Icon name="plus" size="sm" />
                  Create new tag
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create Tag Modal */}
      {showCreateModal && (
        <TagFormModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateTag}
          mode="create"
        />
      )}
    </div>
  );
};
